import type { Rule } from 'ant-design-vue/es/form/interface'

/**
 * 密码正则表达式
 * 要求：8-16位，包含大写字母、小写字母、数字、特殊符号
 */
export const PASSWORD_REGEX = /^(?=.*\d)(?=.*[A-Z])(?=.*[a-z])(?=.*[\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7F])[\da-zA-Z\x21-\x2F\x3A-\x40\x5B-\x60\x7B-\x7F]{8,16}$/

/**
 * 验证用户名
 * @param _rule - 表单规则对象
 * @param value - 输入的用户名
 * @returns Promise
 */
export const validateUserName = async (_rule: Rule, value: string): Promise<void> => {
  if (value.trim() === '') {
    return Promise.reject(new Error('请输入用户名'))
  }
  if (!/^[\w\u4E00-\u9FA5]+$/.test(value)) {
    return Promise.reject(new Error('用户名只支持中文、英文、数字、下划线的组合！'))
  }
  return Promise.resolve()
}

/**
 * 验证组织名称
 * @param _rule - 表单规则对象
 * @param value - 输入的组织名称
 * @returns Promise
 */
export const validateOrgName = async (_rule: Rule, value: string): Promise<void> => {
  if (value.trim() === '') {
    return Promise.reject(new Error('请输入组织名称'))
  }
  return Promise.resolve()
}

/**
 * 验证手机号
 * @param phoneNumber - 手机号
 * @param areaCode - 区号
 * @returns boolean
 */
export const validatePhoneNumber = (phoneNumber: string, areaCode: string): boolean => {
  if (!phoneNumber) return false

  // 中国大陆手机号验证
  if (areaCode === '+86') {
    return /^1[3-9]\d{9}$/.test(phoneNumber)
  }

  // 其他地区手机号验证（简单验证）
  return /^\d{6,15}$/.test(phoneNumber)
}

/**
 * 验证验证码
 * @param _rule - 表单规则对象
 * @param value - 输入的验证码
 * @returns Promise
 */
export const validateVerityCode = async (_rule: Rule, value: string): Promise<void> => {
  if (!value) {
    return Promise.reject(new Error('请输入验证码'))
  }
  return Promise.resolve()
}

/**
 * 验证密码
 * @param _rule - 表单规则对象
 * @param value - 输入的密码
 * @returns Promise
 */
export const validatePassword = async (
  _rule: Rule,
  value: string,
): Promise<void> => {
  if (!value) {
    return Promise.reject(new Error('请输入密码'))
  }
  if (value.length < 8) {
    return Promise.reject(new Error('密码不得小于8位'))
  }
  if (value.length > 16) {
    return Promise.reject(new Error('密码要小于16位'))
  }
  if (!PASSWORD_REGEX.test(value)) {
    return Promise.reject(new Error('⚠️密码需包含大写字母、小写字母、数字、特殊符号'))
  }
  return Promise.resolve()
}

/**
 * 验证邮箱格式
 * @param value - 输入的邮箱
 * @returns boolean
 */
export const validateEmail = (value: string): boolean => {
  if (!value?.trim()) {
    return false
  }
  if (!/^[\w.%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(value)) {
    return false
  }
  return true
}

/**
 * 验证邮箱格式(异步)
 * @param _rule - 表单规则对象
 * @param value - 输入的邮箱
 * @returns Promise
 */
export const asyncValidateEmail = async (_rule: Rule, value: string): Promise<boolean> => {
  if (!value?.trim()) {
    return Promise.reject(new Error('请输入邮箱'))
  }
  if (!validateEmail(value)) {
    return Promise.reject(new Error('邮箱格式不正确'))
  }
  return true
}

/**
 * 验证中国大陆手机号格式
 * @param _rule - 表单规则对象
 * @param value - 输入的手机号
 * @returns Promise
 */
export const validateChinesePhone = async (_rule: Rule, value: string): Promise<void> => {
  if (!value?.trim()) {
    return Promise.reject(new Error('请输入手机号'))
  }
  if (!/^\d{11}$/.test(value)) {
    return Promise.reject(new Error('手机号必须是11位数字'))
  }
  return Promise.resolve()
}

/**
 * 验证登录验证码
 * @param _rule - 表单规则对象
 * @param value - 输入的验证码
 * @returns Promise
 */
export const validateLoginCode = async (_rule: Rule, value: string): Promise<void> => {
  if (!value?.trim()) {
    return Promise.reject(new Error('请输入验证码'))
  }
  return Promise.resolve()
}

/**
 * 手机号校验
 * @param _rule - 表单校验规则
 * @param value - 输入的手机号
 * @param countryCode - 国家码，如 '+86' 表示中国
 * @param required - 是否必填，默认 true
 */
export const validatePhone = async (
  _rule: Rule,
  value: string,
  countryCode: string = '+86',
  required: boolean = true,
  i18nText: {
    请输入手机号: string
    手机号不得小于11位: string
    手机号不得大于11位: string
    请输入正确的手机号: string

  },
): Promise<void> => {
  if (!value) {
    if (required) {
      return Promise.reject(new Error(i18nText['请输入手机号']))
    }
    return Promise.resolve()
  }
  if (countryCode === '+86') {
    if (value.length < 11) {
      return Promise.reject(new Error(i18nText['手机号不得小于11位']))
    } else if (value.length > 11) {
      return Promise.reject(new Error(i18nText['手机号不得大于11位']))
    } else if (!/^\d{11}$/.test(value)) {
      return Promise.reject(new Error(i18nText['请输入正确的手机号']))
    }
  } else {
    if (!/^\d+$/.test(value)) {
      return Promise.reject(new Error('请输入正确的手机号'))
    }
  }
  return Promise.resolve()
}

/**
 * 校验邮箱格式：必须包含且仅包含一个 @，@ 前后可为空
 * @param _rule - 表单规则对象
 * @param value - 输入的邮箱
 * @returns Promise<void>
 */
export const validateSimpleEmailRule = async (_rule: Rule, value: string): Promise<void> => {
  if (!value) {
    // 可为空，直接通过
    return Promise.resolve()
  }
  if (await asyncValidateEmail(_rule, value)) return Promise.resolve()
  return Promise.reject(new Error('邮箱格式不正确'))
}

/**
 * 校验用户名格式：只支持中文、英文、数字、下划线的组合
 * @param _rule - 表单规则对象
 * @param value - 输入的用户名
 * @returns Promise<void>
 */
export const validateSimpleUserNameRule = async (_rule: Rule, value: string): Promise<void> => {
  if (!value) {
    // 可为空，直接通过
    return Promise.resolve()
  }
  if (/^[\w\u4E00-\u9FA5]+$/.test(value)) {
    return Promise.resolve()
  }
  return Promise.reject(new Error('用户名只支持中文、英文、数字、下划线的组合'))
}

/**
 * 校验用户名格式：支持中文、英文、数字、下划线、空格的组合
 * @param _rule - 表单规则对象
 * @param value - 输入的用户名
 * @returns Promise<void>
 */
export const validateUserNameWithSpaceRule = async (_rule: Rule, value: string): Promise<void> => {
  if (!value) return Promise.resolve()
  if (/^[\w\u4E00-\u9FA5 ]+$/.test(value)) return Promise.resolve()
  return Promise.reject(new Error('姓名只支持中文、英文、数字、下划线、空格的组合'))
}

/**
 * 校验英文名格式：支持英文字母、数字、下划线、空格的组合
 * @param _rule - 表单规则对象
 * @param value - 输入的英文名
 * @returns Promise<void>
 */
export const validateEnNameRule = async (_rule: Rule, value: string): Promise<void> => {
  if (!value) return Promise.resolve()
  if (/^[\w ]+$/.test(value)) return Promise.resolve()
  return Promise.reject(new Error('英文名只支持英文字母、数字、下划线、空格的组合'))
}
